<script setup lang="ts">
import SizePills from "@innoclouds/ui/templates/productCard/sizePills/SizePills.vue";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { SIZE_ICON_INACTIVE_COLOR, SIZE_ICON_SIZE } from "#/constants/sizeIcons";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import type { ISizes } from "#/types/iCardProduct";
import { formatPrice, getLocaleAltName, getLocaleName } from "#/utils/i18next";

interface Props {
	sizes: ISizes[];
	selected: ISizes;
	hidePrice?: boolean;
	decoration?: string;
}

type Emits = (e: "choose", size: ISizes) => void;

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { i18next } = useTranslation();

const themeStore = useThemeStore();
const mainStore = useMainStore();
const decor = useDecorationStore().screen(props.decoration);
const isInlinePill = computed(() => decor.variant("size_pill", "stacked") === "inline");
const sizeName = (size: ISizes) => (isInlinePill.value ? getLocaleName(size, i18next.language) : getLocaleAltName(size, i18next.language));

const showPrices = computed(() => !props.hidePrice && themeStore.data?.show_size_picker_prices);
const pillPrice = (size: ISizes) => {
	const symbol = mainStore.config_lib?.currency_symbol;
	if (!isInlinePill.value) return showPrices.value ? formatPrice(size.price ?? 0, symbol) : null;
	if (props.hidePrice || props.selected?.id !== size.id) return null;
	return Number(size.price ?? 0) > 0 ? formatPrice(size.price ?? 0, symbol) : null;
};

const iconColor = (size: ISizes) => (props.selected?.id === size.id ? size.icon_color || null : SIZE_ICON_INACTIVE_COLOR);

const items = computed(() =>
	props.sizes.map((size) => ({
		id: size.id,
		name: sizeName(size) ?? "",
		price: pillPrice(size),
		icon: size.size_image,
		iconColor: iconColor(size),
	})),
);

const onChoose = (id: string | number) => {
	const size = props.sizes.find((item) => item.id === id);
	if (size) emit("choose", size);
};
</script>

<template>
	<SizePills :items="items" :selected-id="selected?.id" :icon-size="SIZE_ICON_SIZE" :decor="decor" @choose="onChoose" />
</template>
